import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { formatDateYYYYMMDD } from '@app/_helpers/functions/date-functions';
import { CommandResult } from '@app/_models/command/commandResult';
import { ContractDto } from '@app/_models/contractDto';
import { ContractForUserDto } from '@app/_models/contractForUserDto';
import { DelegationInterceptor } from '@app/_providers/delegationinterceptor';
import { map, Observable } from 'rxjs';
import { BaseTableDataService } from './baseTableData.service';

@Injectable({
  providedIn: 'root',
})
export class ContractService extends BaseTableDataService<ContractDto, number> {
  constructor() {
    super('contract');
  }

  // We need to fetch 'all' contracts for admins
  private readonly _maxContractSize = 1000;

  /**
   * Get 'all' contracts for admin purposes using the admin query endpoint.
   * This is only needed for displaying / selecting parent contracts, so more than 1000 items would not work anyway.
   */
  getAllContracts(): Observable<ContractDto[]> {
    return this.query('', 0, this._maxContractSize).pipe(map((result) => result.data));
  }

  getUserContractTypes(applyCurrentDelegation = false, userId?: string): Observable<ContractForUserDto[]> {
    let path = '/user/types';
    if (userId) {
      path = `/user/${userId}/types`;
    }
    return this.http.get<ContractForUserDto[]>(this.baseControllerUrl + path, {
      context: new HttpContext().set(DelegationInterceptor.skipDelegatorInterceptor, !applyCurrentDelegation),
    });
  }

  getInvoiceUserContractTypes(invoiceDate?: Date): Observable<ContractForUserDto[]> {
    let params = new HttpParams();

    if (invoiceDate && !Number.isNaN(invoiceDate.getTime())) {
      const invoiceDateString = formatDateYYYYMMDD(invoiceDate);
      params = params.set('invoiceDate', invoiceDateString);
    }

    return this.http.get<ContractForUserDto[]>(this.baseControllerUrl + '/invoice/types', { params: params });
  }

  isInvoiceMenuAvailable(): Observable<boolean> {
    return this.http.get<boolean>(this.baseControllerUrl + '/invoice-menu-available');
  }

  extendContract(contractId: number, validUntilDate: Date): Observable<CommandResult> {
    return this.http.post<CommandResult>(`${this.baseControllerUrl}/extend/`, {
      ContractIds: [contractId],
      ValidUntilDate: formatDateYYYYMMDD(validUntilDate),
    });
  }
}
